/**
 * Simple Wishlist Cart Handler
 * Intercepts add to cart form submissions from the Simple Wishlist app
 * and handles them via AJAX to properly trigger cart drawer and update cart state
 */

class WishlistCartHandler {
  constructor() {
    this.cartDrawer = document.querySelector('cart-drawer');
    this.wishlistPage = null;
    this.retryCount = 0;
    this.maxRetries = 10;
    this.init();
  }

  init() {
    // Wait for the wishlist page to load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.waitForWishlistPage());
    } else {
      this.waitForWishlistPage();
    }
  }

  waitForWishlistPage() {
    // Simple Wishlist app loads content dynamically, so we need to wait for it
    this.wishlistPage = document.querySelector('#st-wishlist-page');
    
    if (this.wishlistPage) {
      // Check if the wishlist page has finished loading (opacity is set to 0 initially)
      const isLoading = this.wishlistPage.style.opacity === '0';
      
      if (isLoading && this.retryCount < this.maxRetries) {
        this.retryCount++;
        setTimeout(() => this.waitForWishlistPage(), 500);
        return;
      }
      
      this.bindEvents();
      this.observeWishlistForms();
    } else if (this.retryCount < this.maxRetries) {
      // Wishlist page not found yet, retry
      this.retryCount++;
      setTimeout(() => this.waitForWishlistPage(), 500);
    }
  }

  bindEvents() {
    this.attachFormHandlers();
    
    // Also listen for view mode changes (grid/list) which might re-render cards
    const viewModeButtons = document.querySelectorAll('.st-js-view-modes button');
    viewModeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        setTimeout(() => this.attachFormHandlers(), 300);
      });
    });
  }

  attachFormHandlers() {
    // Target wishlist product card forms
    const wishlistForms = document.querySelectorAll('#st-wishlist-page .st-product-card_form');
    
    wishlistForms.forEach((form) => {
      // Remove any existing handler to avoid duplicates
      form.removeEventListener('submit', this.handleFormSubmit);
      form.addEventListener('submit', this.handleFormSubmit.bind(this));
    });
  }

  handleFormSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    const submitButton = form.querySelector('button[type="submit"]');
    const productCard = form.closest('.st-product-card');
    
    // Prevent double submissions (check both theme and app loading classes)
    if (submitButton.classList.contains('btn--loading') || 
        productCard?.classList.contains('st-loading')) {
      return;
    }

    // Show loading state
    this.setLoadingState(submitButton, true, productCard);

    // Get form data
    const formData = new FormData(form);
    const body = JSON.stringify(Object.fromEntries(formData));

    // Get sections to update
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { 
        bubbles: true, 
        detail: { sections: sectionsToBundle } 
      })
    );

    const config = {
      ...FoxTheme.utils.fetchConfig('javascript'),
      body: JSON.stringify({
        ...Object.fromEntries(formData),
        sections: sectionsToBundle,
      })
    };

    // Submit via AJAX
    fetch(`${FoxTheme.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          // Handle error
          this.handleError(parsedState, submitButton);
          return;
        }

        // Fetch cart data
        const cartJson = await (
          await fetch(`${FoxTheme.routes.cart_url}`, { 
            ...FoxTheme.utils.fetchConfig() 
          })
        ).json();
        cartJson['sections'] = parsedState['sections'];

        // Update cart state
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { 
          cart: cartJson 
        });

        // Dispatch product added event
        document.dispatchEvent(
          new CustomEvent('product-ajax:added', {
            detail: {
              product: parsedState,
            },
          })
        );

        // Show cart drawer (re-query in case it wasn't available on init)
        const cartDrawer = this.cartDrawer || document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          cartDrawer.show(submitButton);
        } else {
          console.warn('Cart drawer not found or show method not available');
        }
      })
      .catch((error) => {
        console.error('Wishlist add to cart error:', error);
        this.handleError({ description: 'An error occurred' }, submitButton);
      })
      .finally(() => {
        this.setLoadingState(submitButton, false, productCard);
      });
  }

  handleError(parsedState, submitButton) {
    const errorMessage = parsedState.description || parsedState.message || 'An error occurred';
    
    // Show error notification
    if (window.alert) {
      alert(errorMessage);
    }

    // Dispatch error event
    document.dispatchEvent(
      new CustomEvent('product-ajax:error', {
        detail: {
          errorMessage: errorMessage,
        },
      })
    );

    // Publish cart error event
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, {
      source: 'wishlist-form',
      errors: parsedState.errors || parsedState.description,
      message: parsedState.message,
    });
  }

  setLoadingState(button, isLoading, productCard) {
    if (!button) return;

    if (isLoading) {
      button.classList.add('btn--loading');
      button.setAttribute('aria-disabled', 'true');
      // Add loading state to the product card if available
      if (productCard) {
        productCard.classList.add('st-loading');
      }
    } else {
      button.classList.remove('btn--loading');
      button.removeAttribute('aria-disabled');
      // Remove loading state from the product card
      if (productCard) {
        productCard.classList.remove('st-loading');
      }
    }
  }

  observeWishlistForms() {
    // Create a MutationObserver to watch for dynamically added forms
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          // Check if any added nodes contain wishlist forms
          const hasWishlistForms = Array.from(mutation.addedNodes).some((node) => {
            if (node.nodeType === 1) { // Element node
              return node.matches && (
                node.matches('.st-product-card_form') ||
                node.querySelector && node.querySelector('.st-product-card_form')
              );
            }
            return false;
          });

          if (hasWishlistForms) {
            // Re-attach form handlers
            setTimeout(() => this.attachFormHandlers(), 100);
          }
        }
      });
    });

    // Observe the wishlist page container
    const wishlistPage = document.querySelector('#st-wishlist-page');
    if (wishlistPage) {
      observer.observe(wishlistPage, {
        childList: true,
        subtree: true,
      });
    }
  }
}

// Initialize the handler when the DOM is ready
if (typeof FoxTheme !== 'undefined' && FoxTheme.routes) {
  new WishlistCartHandler();
} else {
  // Wait for FoxTheme to be available
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof FoxTheme !== 'undefined' && FoxTheme.routes) {
      new WishlistCartHandler();
    }
  });
}

